import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { signOut } from '../../../services/cognito';
import { featureFlags } from '../../../utils/featureFlags';
import { clearSessionPreservingPreferences } from '../../../utils/sessionStorage';
import JurisdictionSwitcher from '../../JurisdictionSwitcher';
import MarketAwareNavbar from './MarketAwareNavbar';
import { getAppMarket } from '../../../services/MarketService';

interface NavbarProps {
    expiringSoon?: number;
    overdue?: number;
}

// Premium features locked for trial accounts
const PREMIUM_FEATURES = ['evictions', 'agreements', 'aml', 'finance', 'arrears', 'analysis', 'communications', 'repairs-tracking', 'awaab-law'];

const Navbar = ({ expiringSoon = 0, overdue = 0 }: NavbarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    // Texas market renders the market-aware navbar (Texas nav items, no UK-only
    // EPC/HMO/Arrears/Awaab). UK and hub markets keep the full UK dashboard menu.
    if (getAppMarket() === 'texas') {
        return <MarketAwareNavbar expiringSoon={expiringSoon} overdue={overdue} />;
    }

    // Check if user is on trial plan
    const isTrial = localStorage.getItem('subscriptionStatus') === 'trial';
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    const isPremiumLocked = isTrial && !isAdmin;

    // Handle clicks on premium features for trial users
    const handlePremiumClick = (e: React.MouseEvent, feature: string) => {
        if (isPremiumLocked && PREMIUM_FEATURES.includes(feature)) {
            e.preventDefault();
            navigate('/subscription?upgrade=1&feature=' + feature);
        }
    };

    // Returns lock badge JSX for premium nav items
    const LockBadge = () => (
        <span
            title="Upgrade to unlock"
            style={{ fontSize: '11px', marginLeft: '4px', opacity: 0.7 }}
        >
            🔒
        </span>
    );

    const handleLogout = () => {
        signOut();
        clearSessionPreservingPreferences();
        window.location.href = '/';
    };

    const handleAddProperty = () => {
        navigate('/add-property');
    };

    // Helper to determine if a link is active
    const isActive = (path: string) => {
        if (path === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname.startsWith(path);
    };

    // Get active link styles
    const getLinkStyles = (path: string) => {
        const active = isActive(path);
        return active
            ? "text-green-600 font-semibold"
            : "text-gray-700 hover:text-green-600 transition-colors";
    };

    // Total alerts for mobile badge
    const totalAlerts = expiringSoon + overdue;

    return (
        <nav className="bg-white shadow-lg sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <div className="flex items-center space-x-4">
                        {/* Logo removed as per user request */}
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {featureFlags.DASHBOARD_ENABLED && <Link to="/dashboard" className={getLinkStyles('/dashboard')}>Dashboard</Link>}
                        {featureFlags.PROPERTIES_ENABLED && <Link to="/properties" className={getLinkStyles('/properties')}>Properties</Link>}
                        {/* Direct link to Certificates/Compliance Hub */}
                        {featureFlags.CERTIFICATES_ENABLED && (
                            <Link to="/certificates" className={getLinkStyles('/certificates')}>
                                <i className="fas fa-certificate mr-1"></i>Certificates
                            </Link>
                        )}

                        {/* Repairs Tracking (RP01/RP02) — full tab, portfolio-wide repair/SLA
                            progress view. Replaces the F-132 Compliance Command Centre tab;
                            its reporting now lives on the main Dashboard instead. Nested
                            dropdown so Case Linking (F-090) is reachable from here too,
                            rather than adding it as its own top-level tab. */}
                        <div className="relative group">
                            <button
                                className={`flex items-center space-x-1 ${isActive('/repairs-tracking') || isActive('/case-linking') ? 'text-green-600 font-bold' : 'text-gray-700 hover:text-green-600'} transition-all`}
                                onClick={() => setOpenDropdown(openDropdown === 'repairs' ? null : 'repairs')}
                            >
                                <i className="fas fa-toolbox mr-1"></i>
                                <span>Repairs Tracking</span>
                                {isPremiumLocked && <LockBadge />}
                                <i className="fas fa-chevron-down text-[10px] ml-1 opacity-50 group-hover:rotate-180 transition-transform"></i>
                            </button>
                            <div className="absolute left-0 mt-2 w-52 bg-white border border-gray-100 rounded-xl shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 transform translate-y-2 group-hover:translate-y-0">
                                <Link
                                    to="/repairs-tracking"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                    onClick={(e) => handlePremiumClick(e, 'repairs-tracking')}
                                >
                                    <i className="fas fa-toolbox mr-2 w-4"></i> Repairs Queue
                                </Link>
                                <Link
                                    to="/case-linking"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                    onClick={(e) => handlePremiumClick(e, 'repairs-tracking')}
                                >
                                    <i className="fas fa-link mr-2 w-4"></i> Case Linking
                                </Link>
                            </div>
                        </div>

                        {/* Awaab's Law — full tab entry point, not just the small icon in
                            the Tenants list (that icon still works and opens the same panel).
                            Nested dropdown, same reasoning as Repairs Tracking above. */}
                        <div className="relative group">
                            <button
                                className={`flex items-center space-x-1 ${isActive('/awaab-law') || isActive('/case-linking') ? 'text-green-600 font-bold' : 'text-gray-700 hover:text-green-600'} transition-all`}
                                onClick={() => setOpenDropdown(openDropdown === 'awaab' ? null : 'awaab')}
                            >
                                <i className="fas fa-house-chimney-crack mr-1"></i>
                                <span>Awaab's Law</span>
                                {isPremiumLocked && <LockBadge />}
                                <i className="fas fa-chevron-down text-[10px] ml-1 opacity-50 group-hover:rotate-180 transition-transform"></i>
                            </button>
                            <div className="absolute left-0 mt-2 w-52 bg-white border border-gray-100 rounded-xl shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 transform translate-y-2 group-hover:translate-y-0">
                                <Link
                                    to="/awaab-law"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                    onClick={(e) => handlePremiumClick(e, 'awaab-law')}
                                >
                                    <i className="fas fa-house-chimney-crack mr-2 w-4"></i> Hazard Cases
                                </Link>
                                <Link
                                    to="/case-linking"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                    onClick={(e) => handlePremiumClick(e, 'awaab-law')}
                                >
                                    <i className="fas fa-link mr-2 w-4"></i> Case Linking
                                </Link>
                            </div>
                        </div>

                        {/* Nested Tenants Dropdown */}
                        {featureFlags.TENANTS_ENABLED && (
                            <div className="relative group">
                                <button
                                    className={`flex items-center space-x-1 ${isActive('/tenants') || isActive('/agreements') || isActive('/evictions') || isActive('/arrears') || isActive('/aml') ? 'text-green-600 font-bold' : 'text-gray-700 hover:text-green-600'} transition-all`}
                                    onClick={() => setOpenDropdown(openDropdown === 'tenants' ? null : 'tenants')}
                                >
                                    <i className="fas fa-users mr-1"></i>
                                    <span>Tenants</span>
                                    <i className="fas fa-chevron-down text-[10px] ml-1 opacity-50 group-hover:rotate-180 transition-transform"></i>
                                </button>

                                <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 transform translate-y-2 group-hover:translate-y-0">
                                    <Link to="/tenants" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium">
                                        <i className="fas fa-list-ul mr-2 w-4"></i> Tenant List
                                    </Link>
                                    {featureFlags.ARREARS_ENABLED && (
                                        <Link
                                            to="/arrears"
                                            className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                            onClick={(e) => handlePremiumClick(e, 'arrears')}
                                        >
                                            <span>
                                                <i className="fas fa-money-bill-wave mr-2 w-4"></i> Arrears
                                            </span>
                                            {isPremiumLocked && <LockBadge />}
                                        </Link>
                                    )}
                                    <Link
                                        to="/agreements"
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                        onClick={(e) => handlePremiumClick(e, 'agreements')}
                                    >
                                        <i className="fas fa-file-contract mr-2 w-4"></i> Agreements
                                        {isPremiumLocked && <LockBadge />}
                                    </Link>
                                    <Link
                                        to="/evictions"
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                        onClick={(e) => handlePremiumClick(e, 'evictions')}
                                    >
                                        <i className="fas fa-gavel mr-2 w-4"></i> Eviction Workflow
                                        {isPremiumLocked && <LockBadge />}
                                    </Link>
                                    {/* AML moved here from its own top-level tab — it's a
                                        per-tenant identity/risk check, so it belongs in the
                                        Tenants dropdown alongside Arrears/Agreements/Evictions. */}
                                    <Link
                                        to="/aml"
                                        className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                        onClick={(e) => handlePremiumClick(e, 'aml')}
                                    >
                                        <span>
                                            <i className="fas fa-fingerprint mr-2 w-4"></i> AML Checks
                                        </span>
                                        {isPremiumLocked && <LockBadge />}
                                    </Link>
                                </div>
                            </div>
                        )}

                        <Link
                            to="/legal-qa"
                            className={getLinkStyles('/legal-qa')}
                            onClick={(e) => handlePremiumClick(e, 'analysis')}
                        >
                            <i className="fas fa-gavel mr-1"></i>Compliance Advisor
                            {isPremiumLocked && <LockBadge />}
                        </Link>
                        {featureFlags.COMMUNICATIONS_ENABLED && (
                            <Link
                                to="/communications"
                                className={getLinkStyles('/communications')}
                                onClick={(e) => handlePremiumClick(e, 'communications')}
                            >
                                <i className="fas fa-paper-plane mr-1"></i>Messages
                                {isPremiumLocked && <LockBadge />}
                            </Link>
                        )}
                        {featureFlags.FINANCE_ENABLED && (
                            <a
                                href="/dashboard/finance"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={getLinkStyles('/dashboard/finance')}
                                onClick={(e) => handlePremiumClick(e, 'finance')}
                            >
                                <i className="fas fa-chart-line mr-1"></i>Finance
                                {isPremiumLocked && <LockBadge />}
                            </a>
                        )}
                        {featureFlags.TEAM_ENABLED && <Link to="/team" className={getLinkStyles('/team')}>Team</Link>}
                        {/* Calendar for Partner Scheduling - Visible to admins */}
                        {localStorage.getItem('isAdmin') === 'true' && (
                            <Link to="/calendar" className={getLinkStyles('/calendar')}>
                                <i className="fas fa-calendar-alt mr-1"></i>📅 Calendar
                            </Link>
                        )}
                        {localStorage.getItem('isAdmin') === 'true' && (
                            <a
                                href={localStorage.getItem('isSuperAdmin') === 'true' ? "/super-admin" : "/admin-portal"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={getLinkStyles(localStorage.getItem('isSuperAdmin') === 'true' ? '/super-admin' : '/admin-portal')}
                            >
                                <i className="fas fa-user-shield mr-1"></i>
                                {localStorage.getItem('isSuperAdmin') === 'true' ? 'Super Admin' : 'Admin Portal'}
                            </a>
                        )}
                        {/* Branding — visible to anyone with Tenant Admin or Super
                            Admin authority. Checks several stores because the role
                            arrives via different paths depending on the auth flow
                            (Cognito groups, JWT custom:role, impersonation token). */}
                        {(() => {
                            const isAdminFlag      = localStorage.getItem('isAdmin') === 'true';
                            const isSuperAdminFlag = localStorage.getItem('isSuperAdmin') === 'true';
                            const role             = (localStorage.getItem('userRole') || '').toUpperCase();
                            const isAdminRole      = ['ADMIN', 'TENANT_ADMIN', 'TENANT ADMIN', 'SUPER_ADMIN', 'SUPER ADMIN'].includes(role);
                            const showBranding     = isAdminFlag || isSuperAdminFlag || isAdminRole;
                            if (!showBranding) return null;
                            return (
                                <a
                                    href="/settings?tab=branding"
                                    className="text-gray-700 hover:text-green-600 transition-colors"
                                >
                                    <i className="fas fa-palette mr-1"></i>Branding
                                </a>
                            );
                        })()}
                        <div className="flex items-center space-x-4">
                            <JurisdictionSwitcher compact />
                            {location.pathname === '/properties' && (
                                <button
                                    onClick={handleAddProperty}
                                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                                >
                                    <i className="fas fa-plus mr-2"></i>Add Property
                                </button>
                            )}
                            <button
                                onClick={handleLogout}
                                className="text-gray-500 hover:text-red-600 transition-colors font-medium"
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>

                    {/* Mobile: Dashboard-only with Alert Badge (Option B) */}
                    <div className="flex md:hidden items-center space-x-3">
                        <JurisdictionSwitcher compact />
                        {/* Alert Badge - shows if there are warnings */}
                        {totalAlerts > 0 && (
                            <Link
                                to="/dashboard"
                                className="relative bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-medium flex items-center"
                            >
                                <i className="fas fa-exclamation-triangle mr-1.5"></i>
                                {totalAlerts}
                            </Link>
                        )}
                        {location.pathname === '/properties' && (
                            <button
                                onClick={handleAddProperty}
                                className="bg-green-600 text-white w-9 h-9 rounded-full flex items-center justify-center shadow-sm hover:bg-green-700 transition-colors"
                                aria-label="Add Property"
                            >
                                <i className="fas fa-plus text-sm"></i>
                            </button>
                        )}
                        <button
                            onClick={handleLogout}
                            className="text-gray-500 hover:text-red-600 p-2"
                            aria-label="Sign Out"
                        >
                            <i className="fas fa-sign-out-alt text-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
